// Watercolor/ink particle system (Phase 7b).
//
// Particles are full ECS entities with a `particle` component. The system
// here just ages them and despawns when their lifetime is exhausted; world
// drift is encoded in their `drift` (slow paint settle) since they don't go
// through the regular movementSystem.
//
// Emitters (spawnPigmentPuff / spawnInkSplatter / spawnSporeBlob) are called
// from absorption.ts, damage.ts, and hazards.ts at the moment a verb fires.
//
// Ripple emitters live in this file too because the same verbs that spawn
// particles also push wet-paper ripples into the postprocess displacement
// pass. Render.ts pulls the active ripple list and writes it to the post
// uniform buffer each frame; updateRipples() ages and prunes the list.

import type { Entity } from '../components'
import type { GameWorld } from '../world'

export type ParticleKind = 'pigment-puff' | 'ink-splatter' | 'spore-cluster'

const KIND_ID: Record<ParticleKind, number> = {
  'pigment-puff': 0,
  'ink-splatter': 1,
  'spore-cluster': 2,
}

export function particleKindId(kind: ParticleKind): number {
  return KIND_ID[kind]
}

export function particleSystem(world: GameWorld, dt: number): void {
  for (const p of world.with('particle', 'position')) {
    p.particle.age += dt
    if (p.particle.age >= p.particle.lifetime) {
      world.remove(p)
      continue
    }
    // Drift slows over time — paint settles, ink stops bleeding.
    const t = p.particle.age / p.particle.lifetime
    const decay = 1 - t * 0.7
    p.position.x += p.particle.drift.x * dt * decay
    p.position.y += p.particle.drift.y * dt * decay
  }
}

// ── Emitters ──────────────────────────────────────────────────────────────

// Pigment puff: 2–3 small drops of the consumed creature's tint, radiating
// outward and quickly settling. This is a reward that fires constantly
// (every microbe eaten), so it stays small + brief on purpose — should read
// as a pleasant micro-reward, not a punctuating event.
//
// Intentionally does NOT emit a scene ripple: ripples are reserved for
// real impact moments (damage, kill). Otherwise the 8-slot ripple pool
// would saturate inside a couple seconds of eating and the world would
// feel like it's constantly shaking.
export function spawnPigmentPuff(
  world: GameWorld,
  x: number,
  y: number,
  tint: [number, number, number],
  baseSize = 0.05,
): void {
  const n = 2 + Math.floor(Math.random() * 2)  // 2–3 drops
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 + Math.random() * 0.5
    const speed = 0.10 + Math.random() * 0.10
    const size  = baseSize * (0.55 + Math.random() * 0.40)
    world.add({
      position: { x: x + Math.cos(angle) * 0.02, y: y + Math.sin(angle) * 0.02 },
      particle: {
        kind: 'pigment-puff',
        age: 0,
        lifetime: 0.35 + Math.random() * 0.20,  // 0.35–0.55s
        drift: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        spin: 0,
        size,
        tint,
      },
    })
  }
}

// Ink splatter: 6–10 small dark droplets exploding from a hit point.
// Short-lived; drifts faster and settles harder. Reads as a wet ink smear.
export function spawnInkSplatter(world: GameWorld, x: number, y: number): void {
  const n = 6 + Math.floor(Math.random() * 5)
  for (let i = 0; i < n; i++) {
    const angle = Math.random() * Math.PI * 2
    const speed = 0.30 + Math.random() * 0.55
    const size  = 0.025 + Math.random() * 0.030
    world.add({
      position: { x, y },
      particle: {
        kind: 'ink-splatter',
        age: 0,
        lifetime: 0.35 + Math.random() * 0.25,
        drift: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed },
        spin: 0,
        size,
        // Tint is overlaid on inkColor in the shader; pass a small color
        // bias so individual droplets feel slightly heterogeneous.
        tint: [0.10 + Math.random() * 0.05, 0.07 + Math.random() * 0.03, 0.05 + Math.random() * 0.03],
      },
    })
  }
  // Damage ripple is much sharper than a pigment puff — sells the impact.
  emitRipple(x, y, { strength: 0.045, lifetime: 0.5, glitch: true })
}

// Spore cluster: 1–2 drifting blobs near a toxic hazard. Called periodically
// by hazardSystem rather than as a one-shot.
export function spawnSporeBlob(
  world: GameWorld,
  x: number,
  y: number,
  tint: [number, number, number],
): void {
  const angle = Math.random() * Math.PI * 2
  const speed = 0.04 + Math.random() * 0.06
  world.add({
    position: { x, y },
    particle: {
      kind: 'spore-cluster',
      age: 0,
      lifetime: 1.6 + Math.random() * 1.2,
      drift: { x: Math.cos(angle) * speed, y: Math.sin(angle) * speed * 0.5 + 0.04 },
      spin: 0,
      size: 0.04 + Math.random() * 0.025,
      tint,
    },
  })
}

// ── Ripple emitter pool (consumed by postprocess) ─────────────────────────

export interface Ripple {
  x: number
  y: number
  age: number
  lifetime: number
  strength: number
  glitch: boolean // true triggers stronger CA spike (hit feedback)
}

export const MAX_RIPPLES = 8
const ripples: Ripple[] = []

export function emitRipple(
  x: number,
  y: number,
  opts: { strength: number; lifetime: number; glitch?: boolean },
): void {
  if (ripples.length >= MAX_RIPPLES) {
    // Drop the oldest in-progress ripple — fresher events take priority.
    let oldestIdx = 0
    let oldestAgeFrac = 0
    for (let i = 0; i < ripples.length; i++) {
      const t = ripples[i].age / ripples[i].lifetime
      if (t > oldestAgeFrac) { oldestAgeFrac = t; oldestIdx = i }
    }
    ripples.splice(oldestIdx, 1)
  }
  ripples.push({
    x, y,
    age: 0,
    lifetime: opts.lifetime,
    strength: opts.strength,
    glitch: !!opts.glitch,
  })
}

export function updateRipples(dt: number): void {
  for (let i = ripples.length - 1; i >= 0; i--) {
    ripples[i].age += dt
    if (ripples[i].age >= ripples[i].lifetime) ripples.splice(i, 1)
  }
}

export function activeRipples(): readonly Ripple[] {
  return ripples
}

// Reset all in-flight ripples — call on respawn / scene reset so a death
// blast doesn't bleed into the next life.
export function clearRipples(): void {
  ripples.length = 0
}

// Called by external systems (damage, hazards, absorption) to spawn the
// effects associated with an event. Bundling keeps emitter logic in one place
// rather than scattered across systems.
export function emitConsumeEffects(
  world: GameWorld,
  e: Entity,
): void {
  const tint = e.renderable?.tint ?? [0.6, 0.7, 0.9]
  const r = e.renderable?.radius ?? 0.06
  spawnPigmentPuff(world, e.position.x, e.position.y, tint, r * 0.9)
}

export function emitDamageEffects(
  world: GameWorld,
  x: number,
  y: number,
): void {
  spawnInkSplatter(world, x, y)
}
